// ローカルストレージ管理ユーティリティ

import logger from '../utils/logger';
import { 
  ExtendedCustomer, 
  ExtendedReservation,
  generateCustomerCode
} from '../types/salonboard';
import { StaffShiftData, ExtendedStaffShiftData } from '../types/extended-shift';
import { TreatmentRecord, TreatmentRecordInput } from '../types/treatment';
import CryptoJS from 'crypto-js';

const STORAGE_KEYS = {
  CUSTOMERS: 'salonhub_customers',
  RESERVATIONS: 'salonhub_reservations',
  TREATMENTS: 'salonhub_treatments',
  MEDICAL_RECORDS: 'salonhub_medical_records',
  SEQUENCES: 'salonhub_sequences',
  STAFF_SHIFTS: 'salonhub_staff_shifts',
  STAFF_SHIFTS_EXTENDED: 'salonhub_extended_staff_shifts',
} as const;

// 暗号化キー（環境変数から取得、フォールバックあり）
const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || 'SalonHub-PII-Encryption-Key-2025-Dev';

// 暗号化関数
function encrypt<T>(data: T): string {
  const jsonString = JSON.stringify(data);
  return CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString();
}

// 復号化関数
function decrypt<T>(encryptedData: string): T | null {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedString);
  } catch (error) {
    logger.error('Decryption failed:', error);
    return null;
  }
}

// サロンボード互換機能を含む拡張Customer型を使用
export type Customer = ExtendedCustomer;

// 正式なTreatmentRecord型を再エクスポート（types/treatment.tsから）
export type { TreatmentRecord } from '../types/treatment';

// テストモードフラグの定義（関数として定義して実行時に評価）
const isTestMode = () => {
  return import.meta.env.MODE === 'test' || 
         (typeof window !== 'undefined' && window.location?.search?.includes('test=true'));
};

// 顧客データの取得（暗号化対応 + テストモード対応）
export function getCustomers(): Customer[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CUSTOMERS);
    if (!stored) return [];
    
    // テストモードでは暗号化をスキップ
    if (isTestMode()) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        logger.error('Test mode: Failed to parse customers data:', error);
        return [];
      }
    }
    
    // 本番モードでは暗号化処理
    try {
      // まず復号化を試みる
      const decrypted = decrypt<Customer[]>(stored);
      if (decrypted) return decrypted;
    } catch {
      // 復号化に失敗した場合は、平文として扱う（移行期間の対応）
      try {
        return JSON.parse(stored);
      } catch {
        return [];
      }
    }
    
    return [];
  } catch (error) {
    logger.error('Failed to load customers from localStorage:', error);
    return [];
  }
}

// 顧客データの保存（暗号化対応）
export function saveCustomers(customers: Customer[]): void {
  try {
    // テストモードでは暗号化をスキップ
    if (isTestMode()) {
      localStorage.setItem(STORAGE_KEYS.CUSTOMERS, JSON.stringify(customers));
    } else {
      // 顧客データを暗号化して保存
      const encrypted = encrypt(customers);
      localStorage.setItem(STORAGE_KEYS.CUSTOMERS, encrypted);
    }
  } catch (error) {
    logger.error('Failed to save customers to localStorage:', error);
    throw new Error('顧客データの保存に失敗しました');
  }
}

// 予約データの取得（ExtendedReservation型対応）
export function getReservations(): ExtendedReservation[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.RESERVATIONS);
    if (!stored) return [];
    
    const parsed = JSON.parse(stored);
    // 日付フィールドをDateオブジェクトに変換
    return parsed.map((reservation: Record<string, unknown>) => ({
      ...reservation,
      date: new Date(reservation.date as string | number | Date),
      createdAt: reservation.createdAt ? new Date(reservation.createdAt as string | number | Date) : undefined,
      updatedAt: reservation.updatedAt ? new Date(reservation.updatedAt as string | number | Date) : undefined,
    }));
  } catch (error) {
    logger.error('Failed to load reservations from localStorage:', error);
    return [];
  }
}

// 予約データの保存
export function saveReservations(reservations: ExtendedReservation[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.RESERVATIONS, JSON.stringify(reservations));
  } catch (error) {
    logger.error('Failed to save reservations to localStorage:', error);
    throw new Error('予約データの保存に失敗しました');
  }
}

// 新規顧客の追加
export function addCustomer(customerData: Omit<Customer, 'id' | 'createdAt' | 'updatedAt' | 'customerCode' | 'messageSettings' | 'deliveryHistory' | 'mergeHistory' | 'mergeCandidates' | 'customFields'>): Customer {
  const customers = getCustomers();
  const newId = Math.max(...customers.map(c => c.id), 0) + 1;
  
  // シーケンス番号を取得してカスタマーコードを生成
  const sequences = getSequences();
  const customerSequence = (sequences.customer || 0) + 1;
  updateSequence('customer', customerSequence);
  
  const newCustomer: Customer = {
    ...customerData,
    id: newId,
    customerCode: generateCustomerCode(customerSequence),
    messageSettings: {
      email: true,
      myPage: true,
      push: false,
      sms: false
    },
    deliveryHistory: [],
    mergeHistory: {
      mergedFrom: [],
    },
    mergeCandidates: [],
    customFields: {
      other1: '',
      other2: '',
      other3: ''
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  customers.push(newCustomer);
  saveCustomers(customers);
  return newCustomer;
}

// 顧客情報の更新
export function updateCustomer(id: number, updates: Partial<Customer>): Customer {
  const customers = getCustomers();
  const index = customers.findIndex(c => c.id === id);
  
  if (index === -1) {
    throw new Error('指定された顧客が見つかりません');
  }

  // medicalRecordsを除外したupdatesを作成
  const { medicalRecords: newMedicalRecords, ...otherUpdates } = updates;

  // 基本的な更新を適用
  const updatedCustomer = {
    ...customers[index],
    ...otherUpdates,
    updatedAt: new Date().toISOString(),
  };

  // medicalRecordsが更新に含まれている場合、既存の配列と連結する
  if (newMedicalRecords) {
    updatedCustomer.medicalRecords = [
      ...(customers[index].medicalRecords || []),
      ...newMedicalRecords
    ];
  }

  customers[index] = updatedCustomer;

  saveCustomers(customers);
  return customers[index];
}

// 顧客の削除
export function deleteCustomer(id: number): void {
  const customers = getCustomers();
  const filteredCustomers = customers.filter(c => c.id !== id);
  saveCustomers(filteredCustomers);
}

// 施術記録の取得
export function getTreatments(): TreatmentRecord[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.TREATMENTS);
    if (!stored) return [];
    
    const parsed = JSON.parse(stored);
    // 日付フィールドをDateオブジェクトに変換
    return parsed.map((treatment: Record<string, unknown>) => ({
      ...treatment,
      date: new Date(treatment.date as string | number | Date),
      createdAt: new Date(treatment.createdAt as string | number | Date),
      updatedAt: new Date(treatment.updatedAt as string | number | Date),
    }));
  } catch (error) {
    logger.error('Failed to load treatments from localStorage:', error);
    return [];
  }
}

// 施術記録の保存
export function saveTreatments(treatments: TreatmentRecord[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.TREATMENTS, JSON.stringify(treatments));
  } catch (error) {
    logger.error('Failed to save treatments to localStorage:', error);
    throw new Error('施術記録の保存に失敗しました');
  }
}

// 新規施術記録の追加
export function addTreatment(treatmentData: Omit<TreatmentRecord, 'id' | 'createdAt' | 'updatedAt'>): TreatmentRecord {
  const treatments = getTreatments();
  // IDは文字列なので、数値変換してからmax計算し、再度文字列に変換
  const existingIds = treatments.map(t => parseInt(t.id, 10)).filter(id => !isNaN(id));
  const newId = (Math.max(...existingIds, 0) + 1).toString();
  
  const newTreatment: TreatmentRecord = {
    ...treatmentData,
    id: newId,
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  treatments.push(newTreatment);
  saveTreatments(treatments);

  // 顧客の来店履歴も更新
  updateCustomerAfterTreatment(treatmentData.customerId, newTreatment);
  
  return newTreatment;
}

// 施術記録の更新
export function updateTreatment(id: string, updates: Partial<TreatmentRecord>): TreatmentRecord {
  const treatments = getTreatments();
  const index = treatments.findIndex(t => t.id === id);
  
  if (index === -1) {
    throw new Error('指定された施術記録が見つかりません');
  }

  treatments[index] = {
    ...treatments[index],
    ...updates,
    updatedAt: new Date(),
  };

  saveTreatments(treatments);
  return treatments[index];
}

// =================================
// 施術記録 - 顧客ID別管理 (TreatmentService互換)
// =================================

/**
 * 指定顧客の全施術記録を取得 (TreatmentService互換)
 */
export function getTreatmentRecords(customerId: number): TreatmentRecord[] {
  try {
    const key = `treatments_${customerId}`;
    const data = localStorage.getItem(key);
    if (!data) return [];

    const records = JSON.parse(data);
    // Date型フィールドを復元
    return records.map((r: Partial<TreatmentRecord> & { date: string; createdAt: string; updatedAt: string; lastVisitDate?: string }) => ({
      ...r,
      date: new Date(r.date),
      createdAt: new Date(r.createdAt),
      updatedAt: new Date(r.updatedAt),
      lastVisitDate: r.lastVisitDate ? new Date(r.lastVisitDate) : null
    }));
  } catch (error) {
    logger.error('Failed to load treatment records:', error);
    return [];
  }
}

/**
 * 指定顧客の最新施術記録を取得 (TreatmentService互換)
 */
export function getLatestTreatmentRecord(customerId: number): TreatmentRecord | null {
  const records = getTreatmentRecords(customerId);
  return records.length > 0 ? records[0] : null;
}

/**
 * 新規施術記録を保存 (TreatmentService互換)
 */
export function saveTreatmentRecord(customerId: number, record: TreatmentRecordInput): TreatmentRecord {
  try {
    const records = getTreatmentRecords(customerId);
    const newRecord: TreatmentRecord = {
      ...record,
      id: Date.now().toString(),
      customerId,
      date: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      // 必須フィールドのデフォルト値
      totalAmount: record.treatmentAmount || 0,
      visitNumber: records.length + 1,
    };
    
    // 新しい記録を先頭に追加（新しい順）
    records.unshift(newRecord);
    
    const key = `treatments_${customerId}`;
    localStorage.setItem(key, JSON.stringify(records));
    
    return newRecord;
  } catch (error) {
    logger.error('Failed to save treatment record:', error);
    throw new Error('施術記録の保存に失敗しました');
  }
}

/**
 * 既存施術記録を更新 (TreatmentService互換)
 */
export function updateTreatmentRecord(
  customerId: number, 
  id: string, 
  updates: Partial<TreatmentRecord>
): TreatmentRecord | null {
  try {
    const records = getTreatmentRecords(customerId);
    const index = records.findIndex(r => r.id === id);
    
    if (index === -1) {
      logger.error(`Treatment record not found: ${id}`);
      return null;
    }
    
    // 更新データをマージ
    records[index] = {
      ...records[index],
      ...updates,
      updatedAt: new Date(),
    };
    
    const key = `treatments_${customerId}`;
    localStorage.setItem(key, JSON.stringify(records));
    
    return records[index];
  } catch (error) {
    logger.error('Failed to update treatment record:', error);
    throw new Error('施術記録の更新に失敗しました');
  }
}

/**
 * 指定期間の施術記録を取得 (TreatmentService互換)
 */
export function getTreatmentRecordsByDateRange(
  customerId: number,
  startDate: Date,
  endDate: Date
): TreatmentRecord[] {
  const records = getTreatmentRecords(customerId);
  return records.filter(record => {
    const recordDate = new Date(record.date);
    return recordDate >= startDate && recordDate <= endDate;
  });
}

/**
 * 指定顧客の来店回数を取得（現在の訪問を含む） (TreatmentService互換)
 */
export function getVisitCount(customerId: number): number {
  try {
    const records = getTreatmentRecords(customerId);
    return records.length + 1; // 現在の訪問を含む
  } catch (error) {
    logger.error('Failed to get visit count:', error);
    return 1; // エラー時のデフォルト値
  }
}

/**
 * 施術記録を削除 (TreatmentService互換)
 */
export function deleteTreatmentRecord(customerId: number, id: string): boolean {
  try {
    const records = getTreatmentRecords(customerId);
    const filteredRecords = records.filter(r => r.id !== id);
    
    if (records.length === filteredRecords.length) {
      return false; // 削除対象が見つからなかった
    }
    
    const key = `treatments_${customerId}`;
    localStorage.setItem(key, JSON.stringify(filteredRecords));
    
    return true;
  } catch (error) {
    logger.error('Failed to delete treatment record:', error);
    return false;
  }
}

// 施術後の顧客情報更新
function updateCustomerAfterTreatment(customerId: number, treatment: TreatmentRecord): void {
  const customers = getCustomers();
  const customerIndex = customers.findIndex(c => c.id === customerId);
  
  if (customerIndex === -1) return;

  const customer = customers[customerIndex];
  
  // 来店履歴を更新
  if (!customer.visitHistory) {
    customer.visitHistory = [];
  }
  
  // TreatmentRecordをVisitHistory形式に変換
  const visitHistoryEntry = {
    id: treatment.id,
    date: treatment.date.toISOString(),
    services: [treatment.treatmentMenu],
    staff: treatment.staffName,
    duration: treatment.treatmentDuration,
    reservationId: treatment.reservationId || '',
    treatmentNotes: typeof treatment.notes === 'string' ? treatment.notes : 
                   (typeof treatment.notes === 'object' ? 
                    `${treatment.notes.before || ''} ${treatment.notes.during || ''} ${treatment.notes.after || ''}`.trim() : ''),
    products: Array.isArray(treatment.products) ? 
             treatment.products.map(p => typeof p === 'string' ? p : p.name || '') : [],
    nextRecommendation: '',
    amount: treatment.totalAmount,
    status: 'completed'
  };
  customer.visitHistory.push(visitHistoryEntry);
  
  // 統計情報を更新
  customer.totalVisits = customer.visitHistory.length;
  customer.totalSpent = customer.visitHistory.reduce((sum, visit) => sum + ((visit as unknown as Record<string, unknown>).amount as number || 0), 0);
  customer.lastVisit = treatment.date.toISOString();
  customer.status = 'active';
  
  // ランクを再計算（簡単なロジック）
  if (customer.totalSpent >= 300000) {
    customer.rank = 'platinum';
  } else if (customer.totalSpent >= 150000) {
    customer.rank = 'gold';
  } else if (customer.totalSpent >= 50000) {
    customer.rank = 'silver';
  } else {
    customer.rank = 'bronze';
  }
  
  customer.updatedAt = new Date().toISOString();
  
  customers[customerIndex] = customer;
  saveCustomers(customers);
}

// 初期データの設定（モックデータから移行）
export function initializeWithMockData(mockCustomers: Partial<ExtendedCustomer>[]): void {
  const existing = getCustomers();
  if (existing.length === 0) {
    const convertedCustomers: ExtendedCustomer[] = mockCustomers.map(mock => ({
      id: mock.id ?? 0,
      name: mock.name || '',
      nameKana: mock.nameKana || '',
      email: mock.email || '',
      phone: mock.phone || '',
      birthday: mock.birthday || '',
      gender: mock.gender || 'other',
      occupation: mock.occupation || '',
      address: mock.address || '',
      status: 'active' as const,
      totalVisits: (mock as Partial<Customer> & { visitCount?: number }).visitCount || 0,
      ...mock,
      createdAt: new Date(Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAt: new Date().toISOString(),
    } as ExtendedCustomer));
    saveCustomers(convertedCustomers);
  }
}

// データのクリア（開発用）
export function clearAllData(): void {
  localStorage.removeItem(STORAGE_KEYS.CUSTOMERS);
  localStorage.removeItem(STORAGE_KEYS.TREATMENTS);
  localStorage.removeItem(STORAGE_KEYS.RESERVATIONS);
}

// シーケンス番号の管理
export interface Sequences {
  customer?: number;
  reservation_BD?: number;
  reservation_YE?: number;
  reservation_WB?: number;
  reservation_OT?: number;
}

// シーケンス番号の取得
export function getSequences(): Sequences {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SEQUENCES);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    logger.error('Failed to load sequences from localStorage:', error);
    return {};
  }
}

// シーケンス番号の更新
export function updateSequence(key: keyof Sequences, value: number): void {
  const sequences = getSequences();
  sequences[key] = value;
  localStorage.setItem(STORAGE_KEYS.SEQUENCES, JSON.stringify(sequences));
}

// =================================
// スタッフシフト管理
// =================================

// 基本スタッフシフトデータの取得
export function getStaffShifts(): StaffShiftData[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.STAFF_SHIFTS);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    logger.error('Failed to load staff shifts from localStorage:', error);
    return [];
  }
}

// 基本スタッフシフトデータの保存
export function saveStaffShifts(shifts: StaffShiftData[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.STAFF_SHIFTS, JSON.stringify(shifts));
  } catch (error) {
    logger.error('Failed to save staff shifts to localStorage:', error);
    throw error;
  }
}

// 新しいスタッフシフトの追加
export function addStaffShift(shiftData: Omit<StaffShiftData, 'id' | 'createdAt' | 'updatedAt'>): StaffShiftData {
  try {
    const shifts = getStaffShifts();
    const newShift: StaffShiftData = {
      id: `shift_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...shiftData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    shifts.push(newShift);
    saveStaffShifts(shifts);
    return newShift;
  } catch (error) {
    logger.error('Failed to add staff shift:', error);
    throw error;
  }
}

// スタッフシフトの更新
export function updateStaffShift(id: string, updates: Partial<StaffShiftData>): StaffShiftData {
  try {
    const shifts = getStaffShifts();
    const index = shifts.findIndex(shift => shift.id === id);
    
    if (index === -1) {
      throw new Error(`Staff shift with id ${id} not found`);
    }
    
    const updatedShift: StaffShiftData = {
      ...shifts[index],
      ...updates,
      id: shifts[index].id, // IDは変更不可
      createdAt: shifts[index].createdAt, // 作成日時は変更不可
      updatedAt: new Date().toISOString(),
    };
    
    shifts[index] = updatedShift;
    saveStaffShifts(shifts);
    return updatedShift;
  } catch (error) {
    logger.error('Failed to update staff shift:', error);
    throw error;
  }
}

// スタッフシフトの削除
export function deleteStaffShift(id: string): void {
  try {
    const shifts = getStaffShifts();
    const filteredShifts = shifts.filter(shift => shift.id !== id);
    
    if (filteredShifts.length === shifts.length) {
      throw new Error(`Staff shift with id ${id} not found`);
    }
    
    saveStaffShifts(filteredShifts);
  } catch (error) {
    logger.error('Failed to delete staff shift:', error);
    throw error;
  }
}

// 拡張スタッフシフトデータの取得
export function getExtendedStaffShifts(): ExtendedStaffShiftData[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.STAFF_SHIFTS_EXTENDED);
    if (stored) {
      return JSON.parse(stored);
    }
    
    // 拡張データがない場合は基本データから変換
    const basicShifts = getStaffShifts();
    // 注意: 変換ロジックは実際の要件に応じて実装する必要があります
    return basicShifts.map(shift => ({
      ...shift,
      shiftType: shift.workingHours ? 'work' : 'off',
      workDetails: shift.workingHours ? {
        presetType: 'custom',
        baseWorkingHours: shift.workingHours,
        appointments: [],
      } : undefined,
    })) as ExtendedStaffShiftData[];
  } catch (error) {
    logger.error('Failed to load extended staff shifts from localStorage:', error);
    return [];
  }
}

// 拡張スタッフシフトデータの保存
export function saveExtendedStaffShifts(shifts: ExtendedStaffShiftData[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.STAFF_SHIFTS_EXTENDED, JSON.stringify(shifts));
    
    // 後方互換性のため基本データも同期保存
    const basicShifts: StaffShiftData[] = shifts.map(shift => ({
      id: shift.id,
      staffId: shift.staffId,
      date: shift.date,
      workingHours: shift.shiftType === 'work' && shift.workDetails?.baseWorkingHours ? 
        shift.workDetails.baseWorkingHours : null,
      notes: (shift as unknown as Record<string, unknown>).notes as string || '',
      createdAt: shift.createdAt,
      updatedAt: shift.updatedAt,
    }));
    
    saveStaffShifts(basicShifts);
  } catch (error) {
    logger.error('Failed to save extended staff shifts to localStorage:', error);
    throw error;
  }
}

export default {
  getCustomers,
  saveCustomers,
  addCustomer,
  updateCustomer,
  deleteCustomer,
  getTreatments,
  saveTreatments,
  addTreatment,
  updateTreatment,
  getReservations,
  saveReservations,
  getSequences,
  updateSequence,
  initializeWithMockData,
  clearAllData,
  // スタッフシフト管理
  getStaffShifts,
  saveStaffShifts,
  addStaffShift,
  updateStaffShift,
  deleteStaffShift,
  getExtendedStaffShifts,
  saveExtendedStaffShifts,
  // 施術記録 - 顧客ID別管理 (TreatmentService互換)
  getTreatmentRecords,
  getLatestTreatmentRecord,
  saveTreatmentRecord,
  updateTreatmentRecord,
  getTreatmentRecordsByDateRange,
  getVisitCount,
  deleteTreatmentRecord,
};